<template>
  <div class="w-full" data-testid="subscription-popup">
    <!-- Title Section -->
    <div class="mb-5 self-stretch relative w-full flex items-center justify-center gap-2">
      <header class="absolute left-0 top-1/2 -translate-y-1/2">
        <a href="javascript:;" @click="back" class="block p-2 -m-2" data-testid="subscription-back-button">
          <img src="@/assets/icons/arrow-left.svg" class="w-[25px]" alt="Back" />
        </a>
      </header>
      <div class="self-stretch text-center text-white text-2xl font-bold font-['Inter'] leading-[30px]" data-testid="subscription-title">
        {{ $t('plan_title') !== 'plan_title' ? $t('plan_title') : 'Plan' }}
      </div>
    </div>

    <template v-if="subscription && features">
      <div class="rounded-[12px] bg-[#222a33] px-3 py-2 mb-6 font-['Inter']" data-testid="plan-details-section">
        <div class="flex items-center justify-between gap-2 py-2" data-testid="plan-type-row">
          <span class="text-white/90 flex-shrink-0">{{ $t('plan_type') }}</span>
          <span class="text-[#8a8e92] truncate" data-testid="plan-type-value">{{ subscription.title === "Free" ? "Free Trial" : subscription.title }}</span>
        </div>
        <div class="flex items-center justify-between gap-2 py-2" data-testid="plan-start-date-row">
          <span class="text-white/90">{{ $t('plan_start_date') }}</span>
          <span class="text-[#8a8e92]" data-testid="plan-start-date-value">{{ subscription.period_started_at }}</span>
        </div>
        <div class="flex items-center justify-between gap-2 py-2" data-testid="plan-expiration-date-row">
          <span class="text-white/90">{{ $t('plan_expiration_date') }}</span>
          <span class="text-[#8a8e92]" data-testid="plan-expiration-date-value">{{ subscription.period_expired_at }}</span>
        </div>
        <div v-if="subscription.upcoming_changes" class="flex items-start gap-2 py-2 text-[0.85rem] text-white/90" data-testid="plan-upcoming-changes">
          <img src="@/assets/icons/info-icon.svg" alt="" class="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span v-html="upcomingText"></span>
        </div>
      </div>

      <div class="rounded-[12px] bg-[#222a33] px-3 py-2 mb-6 font-['Inter']" data-testid="plan-credits-section">
        <div class="text-[#8a8e92] text-[0.85rem] font-normal">{{ $t('plan_credit') }}</div>
        <div class="flex items-center justify-between gap-2 py-2" data-testid="sessions-remaining-row">
          <span class="text-white/90">{{ $t('plan_sessions_remaining') }}</span>
          <span class="text-[#8a8e92] whitespace-nowrap">
            <template v-if="features.host_event.total_allowed_items >= 999">{{ $t('common_unlimited') }}</template>
            <template v-else>{{ features.host_event.left_allowed_items }} / {{ features.host_event.total_allowed_items }}</template>
          </span>
        </div>
        <div class="flex items-center justify-between gap-2 py-2" data-testid="asr-remaining-row">
          <span class="text-white/90">{{ $t('plan_asr_remaining') }}</span>
          <span class="text-[#8a8e92] whitespace-nowrap" data-testid="asr-remaining-value">{{ formatSeconds(features.asr.left_allowed_items) }}</span>
        </div>
      </div>

      <div v-if="resources && resources.length" class="rounded-[12px] bg-[#222a33] px-3 py-2 mb-6 font-['Inter']" data-testid="resources-section">
        <div class="text-[#8a8e92] text-[0.85rem] font-normal">{{ $t('resources') }}</div>
        <div class="flex items-center justify-between gap-2 py-2" v-for="resource in resources" :key="resource.id" data-testid="resource-row">
          <span class="text-white/90 min-w-0">
            {{ resource.title }}
            <span class="text-[#8a8e92] text-[0.85rem] ml-1">{{ resource.product_type }}</span>
          </span>
          <span class="text-[#8a8e92] whitespace-nowrap">{{ formatSeconds(resource.total_resources - resource.used_resources) }}</span>
        </div>
      </div>

      <div class="rounded-[12px] bg-[#222a33] px-3 py-2 mb-6 font-['Inter']" data-testid="plan-features-section">
        <div class="text-[#8a8e92] text-[0.85rem] font-normal">{{ $t('plan_features') }}</div>
        <div class="flex items-center justify-between gap-2 py-2" v-for="feature in featureRows" :key="feature.testid" :data-testid="feature.testid">
          <span class="text-white/90">{{ feature.label }}</span>
          <span class="text-[#8a8e92] whitespace-nowrap">{{ availabilityStatus(feature.available) }}</span>
        </div>
      </div>

      <router-link to="/plans" @click="selfClose()" class="block w-full rounded-[12px] bg-primary py-3 text-center text-black text-base font-semibold font-['Inter']" data-testid="see-plans-button">
        {{ $t('common_see_plans') }}
      </router-link>
      <div v-if="canCancel" class="flex justify-center mt-4 mb-2">
        <a href="javascript:;" class="text-[#EA4335] no-underline flex items-center whitespace-nowrap" @click="cancelSubscription" data-testid="cancel-subscription-button">
          <img class="max-w-[20px] mr-[10px]" src="@/assets/icons/sub-cancel-icon.svg" alt="">
          {{ $t('profile_cancel_subscription') }}
        </a>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import emitter from "@/services/emitter.service";
import ProductService from '@/services/products.service';

export default {
  name: 'SubscriptionPopup',
  emits: ['close'],
  methods: {
    back() {
      this.selfClose();
      emitter.emit('open-account-popup');
    },
    selfClose() {
      this.$emitter.emit('modalClose');
    },
    availabilityStatus(bool) {
      return bool ? this.$t('common_available') : this.$t('common_unavailable');
    },
    formatSeconds(seconds) {
      return Math.floor(seconds / 60 / 60) + this.$t('common_hours_short') + ' '
          + Math.floor(seconds / 60 % 60) + this.$t('common_minutes_short') + ' '
          + seconds % 60 + this.$t('common_seconds_short');
    },
    cancelSubscription() {
      if (this.subscription.payment_type != 'stripe' && this.subscription.payment_type.length > 0) {
        this.$toast.error('apple_subscription_notice')
        return false;
      }

      this.$dialog.confirm('profile_subscription_cancel_warning', {
        confirmLabel: 'common_yes',
      }).then((confirmed) => {
        if (confirmed) {
          ProductService.cancel().then(() => {
            this.$dialog.alert('profile_subscription_cancel_success', {
              icon: "success"
            }).then(() => {
              location.reload();
            })
          })
        }
      })
    },
  },
  computed: {
    ...mapState('auth', ['token']),
    ...mapState('nonpersist', ['user']),
    subscription() {
      return this.user?.subscription;
    },
    features() {
      return this.user?.features;
    },
    resources() {
      return this.user?.resources;
    },
    featureRows() {
      return [
        { testid: 'transcripts-feature-row', label: this.$t('transcripts_title'), available: this.features.transcripts.allowed },
        { testid: 'continuous-recording-feature-row', label: this.$t('session_setting_translate_after_pause'), available: this.features.asr.continuous_recording },
        { testid: 'real-time-translation-feature-row', label: this.$t('session_setting_translate_live'), available: this.features.asr.real_time_translation },
        { testid: 'speech-synthesis-feature-row', label: this.$t('speech_synthesis'), available: this.features.custom_terms.allowed },
      ];
    },
    upcomingText() {
      const planString = "<b style='text-transform: capitalize;'>" + this.$t('plans_' + this.subscription.upcoming_changes.type + '_title') + "</b>";
      return this.$t('profile_upcoming_changes', {
        plan: planString,
        date: this.subscription.upcoming_changes.date
      });
    },
    isCanceled() {
      return this.subscription.upcoming_changes && this.subscription.upcoming_changes.type == 'basic';
    },
    canCancel() {
      return this.subscription.type != 'basic' && !this.isCanceled && this.subscription.type != 'organizations_participant';
    },
  },
  async mounted() {
    // fresh plan data, e.g. right after a trial was requested on /plans
    if (this.token) {
      await this.$store.dispatch('auth/userMe', {
        token: this.token,
        room: false,
      });
    }
  },
}
</script>
